import { NetPingDeviceBarrierService } from '@zaoblako/web/core/services/control/net-ping-device-barrier.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';

import { TooltipModule } from 'primeng/tooltip';
import { take } from 'rxjs/operators';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
  NgZone,
  type OnInit,
  type OnDestroy,
  type OnChanges,
  type SimpleChanges
} from '@angular/core';

const GET_STATUS_INTERVAL = 250;

enum StateIcon {
  UPDATING = 'pi pi-spinner pi-spin',
  OPENED = 'pi pi-lock-open',
  CLOSED = 'pi pi-lock',
  ERROR = 'pi pi-exclamation-triangle',
}

@Component({
  standalone: true,
  selector: 'crm-net-ping-device-barrier-status',
  templateUrl: './net-ping-device-barrier-status.component.html',
  styleUrl: './net-ping-device-barrier-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NetPingDeviceBarrierService],
  imports: [TooltipModule]
})
export class NetPingDeviceBarrierStatusComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public styleClass?: string;

  /** Device unique ID */
  @Input() public deviceId?: number;

  /** Current updating timer */
  private timer: ReturnType<typeof setTimeout> = null;

  // State fields

  /** Icon to show */
  public icon: StateIcon = StateIcon.UPDATING;

  /** Tooltip to show */
  public tooltip: string = 'Статус обновляется';

  public constructor(
    private readonly authService: AuthService,
    private readonly netPingDeviceBarrierService: NetPingDeviceBarrierService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {}

  public ngOnInit(): void {
    this.init();
  }

  public ngOnDestroy(): void {
    this.destroy();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const deviceIdChanged = (
      !changes?.deviceId?.isFirstChange() &&
      changes?.deviceId?.currentValue !== changes?.deviceId?.previousValue
    );
    if (deviceIdChanged) {
      this.destroy();
      this.init();
    }
  }

  /** Initialize component */
  private init() {
    if (!this.authService.getToken) {
      this.icon = StateIcon.ERROR;
      this.tooltip = 'Пользователь на авторизован';
      this.changeDetectorRef.markForCheck();
      return;
    }

    if (!this.deviceId) {
      this.icon = StateIcon.ERROR;
      this.tooltip = 'Не указан номер устройства';
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.netPingDeviceBarrierService.init(this.authService.getToken, this.deviceId);

    this.ngZone.runOutsideAngular(() => {
      this.scheduleNextRefresh();
    });
  }

  /**  */
  private destroy() {
    this.cancelNextRefresh();
    this.netPingDeviceBarrierService.destroy();
  }

  /** Cancel next refresh task if defined */
  private cancelNextRefresh() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Schedule next refresj task if defined */
  private scheduleNextRefresh() {
    this.cancelNextRefresh();

    this.timer = setTimeout(
      () => this.refreshStatus(),
      GET_STATUS_INTERVAL
    );
  }

  /** Refresh task status */
  private refreshStatus() {
    this.netPingDeviceBarrierService
      .getBarrierStatus()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.ngZone.run(() => {
            if (response.success) {
              if (response.result.error) {
                this.icon = StateIcon.ERROR;
                this.tooltip = response.result.error;
              } else {
                this.icon = response.result.isBarrierOpened
                  ? StateIcon.OPENED
                  : StateIcon.CLOSED;
                this.tooltip = response.result.isBarrierOpened
                  ? 'Шлагбаум открыт'
                  : 'Шлагбаум закрыт';
              }
            } else {
              this.icon = StateIcon.ERROR;
              this.tooltip = response.error?.message || 'Неизвестная ошибка';
            }
            this.changeDetectorRef.markForCheck();
          });

          this.scheduleNextRefresh();
        },
        error: (error) => {
          this.ngZone.run(() => {
            this.icon = StateIcon.ERROR;
            this.tooltip = typeof error === 'string' ? error : 'Ошибка сети';
            this.changeDetectorRef.markForCheck();
          });

          this.scheduleNextRefresh();
        }
      });
  }
}
